import { useCallback, useMemo } from 'react'
import { useDispatch } from 'react-redux'

import { isGroup } from '../helpers/groups'
import { adjustPositions } from '../helpers/order'
import { useRearrangeFieldsMutation } from '../services/formField'
import { useActionType } from './useActionType'
import { useManageField } from './useManageFields'

export function useManagePositions() {
  const { formId } = useActionType()

  const { fields, selectedFieldId, currentField, getFieldById } = useManageField()

  const dispatch = useDispatch()

  const [rearrangeFields] = useRearrangeFieldsMutation()

  const updateFieldPosition = useCallback(
    async ({ fieldId, targetFieldRow }) => {
      const updatedFields = adjustPositions({
        fields,
        sourceFieldRow: fields.entities[fieldId].fieldRow,
        targetFieldRow
      })

      await rearrangeFields({ formId, payload: updatedFields })
    },
    [fields, dispatch, formId]
  )

  const rearrangeFieldsOnDrag = ({ dragItem, items }) => {
    let updatedFields = []

    const sourceField = getFieldById(dragItem.id)
    const sourceFieldGroup = sourceField?.fieldGroup
    let targetFieldGroup = null
    let targetFieldRow = null
    let targetGroupType = null

    let currentFieldRow = 0
    for (const field of items) {
      if (field.isGroup) {
        const groupField = fields.entities[field.id]
        const groupChildren = field.children.map((child) => {
          currentFieldRow += 1

          let updatedField = { id: child.id, fieldRow: currentFieldRow }
          if (child.id === dragItem.id) {
            targetFieldRow = currentFieldRow
            targetFieldGroup = groupField.fieldGroup
            targetGroupType = groupField.groupType

            updatedField = {
              ...updatedField,
              fieldGroup: targetFieldGroup,
              groupType: targetGroupType
            }
          }

          updatedFields.push(updatedField)
          return updatedField
        })

        updatedFields.push({ id: field.id, children: groupChildren })
      } else {
        currentFieldRow += 1

        if (field.id === dragItem.id) {
          targetFieldGroup = null
          targetFieldRow = currentFieldRow
          targetGroupType = null

          updatedFields.push({
            id: field.id,
            fieldRow: currentFieldRow,
            fieldGroup: targetFieldGroup,
            groupType: targetGroupType
          })
        } else {
          updatedFields.push({ id: field.id, fieldRow: currentFieldRow })
        }
      }
    }

    if (!sourceField.isGroup && sourceFieldGroup !== targetFieldGroup) {
      updatedFields.push({
        id: dragItem.id,
        fieldGroup: targetFieldGroup,
        groupType: targetGroupType
      })

      if (sourceFieldGroup !== null) {
        const groupId = fields.ids.find((id) => isGroup(id) && getFieldById(id).fieldGroup === sourceFieldGroup)
        updatedFields.push({
          id: groupId,
          children: getFieldById(groupId).children.filter(({ id }) => id !== dragItem.id)
        })
      }

      if (targetFieldGroup !== null) {
        const groupId = fields.ids.find((id) => isGroup(id) && getFieldById(id).fieldGroup === targetFieldGroup)
        const childrens = [
          ...getFieldById(groupId).children,
          {
            id: dragItem.id,
            fieldRow: targetFieldRow,
            groupType: targetGroupType
          }
        ]
        updatedFields.push({
          id: groupId,
          children: childrens.sort((a, b) => a.fieldRow - b.fieldRow)
        })
      }
    }

    return {
      sourceField,
      sourceFieldGroup,
      targetFieldRow,
      targetFieldGroup,
      updatedFields
    }
  }

  const updateFieldsAfterDrag = async (updatedFields) => {
    await rearrangeFields({ formId, payload: updatedFields })
  }

  const positions = useMemo(() => {
    if (!fields) return []

    let positions = []

    const fieldGroup = fields.entities[selectedFieldId]?.fieldGroup || null
    if (fieldGroup !== null) {
      const groupId = fields.ids.find((id) => isGroup(id) && getFieldById(id).fieldGroup === fieldGroup)
      positions = getFieldById(groupId)?.children.map(({ id }) => getFieldById(id).fieldRow)
    } else {
      positions = fields.ids.reduce((positions, id) => {
        if (getFieldById(id).fieldGroup === null) positions.push(getFieldById(id).fieldRow)
        return positions
      }, [])
    }

    return positions?.sort((a, b) => a - b) || []
  }, [fields?.ids, currentField])

  return {
    positions,
    updateFieldPosition,
    updateFieldsAfterDrag,
    rearrangeFieldsOnDrag
  }
}
